import {Component, computed, effect, inject, signal, viewChild} from '@angular/core';
import {TranslatePipe} from '@ngx-translate/core';
import {FormControl, FormGroup} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {debounceTime} from 'rxjs';
import {AccountingAccountForm} from '../accounting-account-form/accounting-account-form';
import {ElciaButton} from '../../../../../../../../components/buttons/button/button';
import {Search} from '../../../../../../../../components/search/search';
import {ElciaDialog} from '../../../../../../../../components/dialog/dialog';
import {Loader} from '../../../../../../../../components/loader/loader';
import {FilterContainer} from '../../../../../../../../components/filter-container/filter-container';

import {ConfirmService} from '../../../../../../../../services/confirm.service';
import {
  AccountingAccountFilter,
  AccountingAccountSummary,
  AccountType,
  CreateAccountingAccountRequest,
  UpdateAccountingAccountRequest
} from '../../../../../../../../../api';
import {AccountingAccountFormValue} from '../../models/accounting-account-form-value';

import {AccountingAccountGrid} from '../accounting-account-grid/accounting-account-grid';
import {AccountTypeDropdown} from '../account-type-dropdown/account-type-dropdown';
import {AccountingAccountStoreService} from '../../services/accounting-account-store.service';


@Component({
  selector: 'elcia-accounting-account-home',
  imports: [TranslatePipe, ElciaButton, Search, ElciaDialog, Loader, FilterContainer, AccountTypeDropdown, AccountingAccountGrid, AccountingAccountForm],
  templateUrl: './accounting-account-home.html',
})
export class AccountingAccountHome {
  protected readonly store = inject(AccountingAccountStoreService);
  private readonly confirmService = inject(ConfirmService);

  accountingCodeForm = viewChild<AccountingAccountForm>(AccountingAccountForm);
  isLoading = this.store.isLoading;
  isLoadingSelectedAccountingAccount = this.store.isLoadingSelectedAccountingCode;
  accountingAccounts = this.store.accountingCodes;

  protected isFormDisplayed = signal(false);
  protected isFilterDisplayed = signal(false);
  private openMode = signal<'create' | 'edit'>('create');

  filterForm = new FormGroup({
    searchTerm: new FormControl('', {nonNullable: true}),
    accountTypes: new FormControl<AccountType[] | null>(null),
  });

  private readonly filterValue = toSignal(
    this.filterForm.valueChanges.pipe(debounceTime(300)),
    {initialValue: this.filterForm.value}
  );

  private readonly syncFilter = effect(() => {
    const form = this.filterValue();
    this.store.filterParams.set({
      searchTerm: form.searchTerm || null,
      accountTypes: form.accountTypes ?? null,
    } satisfies AccountingAccountFilter);
  });

  protected isFormSaving = computed(() =>
    this.store.createAccountingAccountMutation.isPending() || this.store.updateAccountingAccountMutation.isPending()
  );
  protected isEditMode = computed(() => this.openMode() === 'edit');

  protected showForm(): void {
    this.openMode.set('create');
    this.store.selectedAccountingCodeId.set(null);
    this.isFormDisplayed.set(true);
  }

  protected closeForm(): void {
    this.isFormDisplayed.set(false);
    this.store.selectedAccountingCodeId.set(null);
  }

  protected onDialogSave(): void {
    this.accountingCodeForm()?.submit();
  }

  protected toggleFilterDisplayed(): void {
    this.isFilterDisplayed.update(v => !v);
  }

  protected onAccountingCodeSelect(accountingSummary: AccountingAccountSummary): void {
    this.openMode.set('edit');
    this.store.selectedAccountingCodeId.set(accountingSummary.id);
  }

  protected onAccountingCodeDelete(accountSummary: AccountingAccountSummary): void {
    this.confirmService.confirm({
      title: 'entity.accounting-account',
      message: accountSummary.name,
      accept: async () => {
        await this.store.deleteAccountingAccountMutation.mutateAsync(accountSummary.id);
      }
    });
  }

  protected deleteAccountingCode(): void {
    const accountingCode = this.store.selectedAccountingAccount();
    if (!accountingCode?.id) return;
    this.confirmService.confirm({
      title: 'entity.accounting-account',
      message: accountingCode.name,
      accept: async () => {
        await this.store.deleteAccountingAccountMutation.mutateAsync(accountingCode.id);
        this.closeForm();
      }
    });
  }

  protected submit(formValue: AccountingAccountFormValue): void {
    const isEdition = this.openMode() === 'edit'
      && formValue.id !== undefined
      && formValue.id !== null
      && formValue.id !== 0;

    if (isEdition) {
      const body: UpdateAccountingAccountRequest = {
        name: formValue.name,
        account: formValue.account,
        accountType: formValue.accountType,
      };
      this.store.updateAccountingAccountMutation.mutate({id: formValue.id!, body});
    } else {
      const request: CreateAccountingAccountRequest = {
        name: formValue.name,
        account: formValue.account,
        accountType: formValue.accountType,
      };
      this.store.createAccountingAccountMutation.mutate(request);
    }
  }

  private readonly closeFormOnSuccess = effect(() => {
    const createSuccess = this.store.createAccountingAccountMutation.isSuccess();
    const updateSuccess = this.store.updateAccountingAccountMutation.isSuccess();
    if (createSuccess || updateSuccess) {
      this.closeForm();
    }
  });

  private readonly openFormOnEdit = effect(() => {
    const selectedAccountingCode = this.store.selectedAccountingAccount();
    if (!selectedAccountingCode) return;
    this.isFormDisplayed.set(true);
  });
}
